import { UserWithPictureTc, useUserFindOneLazyQuery } from "gql/generated";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  FieldValue,
  FieldValues,
  PathValue,
  UseFormReturn,
} from "react-hook-form";
import { z } from "zod";
import { ControlledInputProps } from "shared/components";

export enum ExistingUserEmailStatus {
  None = "NONE",
  OkToAdd = "OK_TO_ADD",
  AlreadyAttached = "ALREADY_ATTACHED",
  Duplicate = "DUPLICATE",
}

type UseInviteStaffMemberProps<T extends FieldValues> = {
  user?: Partial<UserWithPictureTc>;
  entityId: string;
  form: UseFormReturn<T>;
  inputNameEmailProps: ControlledInputProps;
  inputNameFirstNameProps: ControlledInputProps;
  inputNameLastNameProps: ControlledInputProps;
  onExistingUserEmailStatusChange: (status: ExistingUserEmailStatus) => void;
};

export const useInviteStaffMember = <T extends FieldValues>({
  user,
  entityId,
  form,
  inputNameEmailProps,
  inputNameFirstNameProps,
  inputNameLastNameProps,
  onExistingUserEmailStatusChange,
}: UseInviteStaffMemberProps<T>) => {
  const [findUserByEmail] = useUserFindOneLazyQuery();

  const [existingUserEmailStatus, setExistingUserEmailStatus] =
    useState<ExistingUserEmailStatus>(ExistingUserEmailStatus.None);

  const [namesDisabled, setNamesDisabled] = useState(false);
  const [isValidatingEmail, setIsValidatingEmail] = useState(false);

  const lastEmailSearched = useRef<string>("");
  const isInitialMount = useRef<boolean>(true);
  
  // Reset the initial mount flag when user changes
  useEffect(() => {
    isInitialMount.current = true;
    lastEmailSearched.current = "";
  }, [user?._id]);
  
  const handleValidateEmail = useCallback(async () => {
    const email = form.getValues(inputNameEmailProps.name as FieldValue<T>);
    if (!email || email === lastEmailSearched.current) {
      return;
    }
    // Skip validation on initial mount when editing an existing user
    // This prevents false "already attached" errors when the modal opens
    if (isInitialMount.current && user) {
      isInitialMount.current = false;
      lastEmailSearched.current = email;
      return;
    }
    lastEmailSearched.current = email;
    const emailSchema = z.string().min(1).email();

    let newExistingUserEmailStatus = ExistingUserEmailStatus.None;
    setExistingUserEmailStatus(newExistingUserEmailStatus);
    setNamesDisabled(true);
    setIsValidatingEmail(true);
    
    try {
      emailSchema.parse(email);
      
      // Add a minimum delay to provide visual feedback
      const [existingUser] = await Promise.all([
        findUserByEmail({
          variables: { filter: { email } },
        }),
        new Promise<void>(resolve => setTimeout(resolve, 500)), // 500ms minimum delay
      ]);

      if (
        existingUser &&
        existingUser.data &&
        existingUser.data.userFindOne?._id
      ) {
        // If we're updating an existing user's email address
        // and the email address entered already exists for a different user
        // set the existing user status to duplicate
        if (user && user._id !== existingUser.data.userFindOne._id) {
          newExistingUserEmailStatus = ExistingUserEmailStatus.Duplicate;
        } else {
          // Check to see if the existingUser is already attached to this entity
          // BUT skip this check if we're editing the same user (it's expected they're attached)
          const isEditingSameUser = user && user._id === existingUser.data.userFindOne._id;
          const isAlreadyAttached = existingUser.data.userFindOne.access.operators?.find(
            item => item?.entityId === entityId,
          ) ||
          existingUser.data.userFindOne.access.organizations?.find(
            item => item?.entityId === entityId,
          ) ||
          existingUser.data.userFindOne.access.storefronts?.find(
            item => item?.entityId === entityId,
          );
          
          if (isAlreadyAttached && !isEditingSameUser) {
            newExistingUserEmailStatus =
              ExistingUserEmailStatus.AlreadyAttached;
            form.setValue(
              inputNameEmailProps.name as FieldValue<T>,
              "" as PathValue<T, FieldValue<T>>,
              { shouldDirty: true },
            );
          } else {
            newExistingUserEmailStatus = ExistingUserEmailStatus.OkToAdd;
            form.setValue(
              inputNameFirstNameProps.name as FieldValue<T>,
              existingUser.data.userFindOne.firstName as PathValue<
                T,
                FieldValue<T>
              >,
              { shouldDirty: true },
            );
            form.setValue(
              inputNameLastNameProps.name as FieldValue<T>,
              existingUser.data.userFindOne.lastName as PathValue<
                T,
                FieldValue<T>
              >,
              { shouldDirty: true },
            );
          }
        }
        form.clearErrors(inputNameFirstNameProps.name as FieldValue<T>);
        form.clearErrors(inputNameLastNameProps.name as FieldValue<T>);
      } else {
        setNamesDisabled(false);
      }
    } catch {
      // Ensure the names are editable
      setNamesDisabled(false);
      form.clearErrors(inputNameFirstNameProps.name as FieldValue<T>);
      form.clearErrors(inputNameLastNameProps.name as FieldValue<T>);
    } finally {
      setIsValidatingEmail(false);
    }

    setExistingUserEmailStatus(newExistingUserEmailStatus);
    onExistingUserEmailStatusChange(newExistingUserEmailStatus);
    setIsValidatingEmail(false);
  }, [
    user,
    entityId,
    form,
    inputNameEmailProps,
    inputNameFirstNameProps,
    inputNameLastNameProps,
    findUserByEmail,
    onExistingUserEmailStatusChange,
  ]);

  return {
    existingUserEmailStatus,
    namesDisabled,
    isValidatingEmail,
    handleValidateEmail,
  };
};
